import type { CategoryContent } from '../row.js';

/** Art, Music and Screen, second chunk. Correct option first; rotated at build. */

export const MORE_ART: CategoryContent = {
  msc: [
    [
      'What is chiaroscuro?',
      [
        'The modelling of form through strong contrasts of light and dark',
        'The imperceptible blending of tones into one another',
        'The application of paint in thick, textured strokes',
        'The underdrawing visible beneath a thin paint film',
      ],
      0,
      'Sfumato is the blending, impasto the thick application. Caravaggio\'s extreme version is sometimes distinguished as tenebrism.',
    ],
    [
      'What does the term "contrapposto" describe?',
      [
        'A figure whose weight rests on one leg, producing counter-rotation through the torso',
        'Two figures arranged in mirrored symmetry across a central axis',
        'A composition organised around opposed diagonals',
        'A pose in which the head turns against the direction of movement',
      ],
      0,
      'The Doryphoros is the canonical example, and its rediscovery is a large part of what "classical" meant to the Renaissance.',
    ],
    [
      'The Arnolfini Portrait is by whom?',
      [
        'Jan van Eyck',
        'Rogier van der Weyden',
        'Robert Campin, the Master of Flemalle',
        'Hans Memling',
      ],
      0,
      '1434. Panofsky\'s reading of its objects as disguised symbolism is as famous as the painting, and now widely qualified.',
    ],
    [
      'What is an "aedicule" in architecture?',
      [
        'A framed niche or opening flanked by columns and topped by a pediment',
        'A small domed structure raised over a crossing',
        'A recessed panel in a vault or ceiling',
        'A projecting upper storey supported on brackets',
      ],
      0,
      'The recessed panel is a coffer. Aedicules are how classical architecture makes a wall opening into an event.',
    ],
    [
      'Japonisme refers to what?',
      [
        'The influence of Japanese art on European painting and design after the 1850s',
        'The Japanese adoption of Western oil painting in the Meiji period',
        'A nineteenth-century European fashion for Chinese and Japanese porcelain',
        'The school of Japanese printmakers working for the export market',
      ],
      0,
      'Ukiyo-e composition - cropping, flatness, high viewpoints - is visible in Degas, Whistler and Van Gogh directly.',
    ],
    [
      'What is the defining feature of Gothic rib vaulting?',
      [
        'Loads are concentrated on ribs and thence to piers, freeing the web and the wall',
        'The vault is built without formwork, in self-supporting courses',
        'The vault\'s profile is a true catenary curve',
        'The vault is constructed entirely of cast concrete',
      ],
      0,
      'Concentrating thrust is what permits the window area, which is the actual point of the whole system.',
    ],
    [
      'The Vienna Secession was founded in opposition to what?',
      [
        'The conservatism of the Künstlerhaus and the academic establishment',
        'The Bauhaus programme of merging art and industry',
        'The dominance of French Impressionism in Austrian collections',
        'The state\'s refusal to fund public commissions for living artists',
      ],
      0,
      'Founded in 1897 with Klimt among its members, the Secession predates the Bauhaus by two decades, so the two movements never actually overlapped despite both being frequently cited as landmarks of the same broader turn against academic art.',
    ],
    [
      'What is a "cartoon" in the context of fresco or tapestry production?',
      [
        'A full-scale preparatory drawing transferred to the working surface',
        'A satirical drawing published in the periodical press',
        'A small-scale colour study made before the full design',
        'A pattern book of standard motifs kept in a workshop',
      ],
      0,
      'From cartone, heavy paper. Raphael\'s tapestry cartoons survive and the tapestries woven from them are the copies, which reverses the usual hierarchy.',
    ],
    [
      'The term "readymade" is associated with whom?',
      [
        'Marcel Duchamp',
        'Kurt Schwitters, in his Merz assemblages',
        'Man Ray, in his photographic objects',
        'Jean Arp, in his chance collages',
      ],
      0,
      'Fountain, 1917, and the argument it started about whether the gesture or the object is the work.',
    ],
    [
      'What does "iconography" study, in Panofsky\'s sense?',
      [
        'The identification of subject matter and its conventional meanings',
        'The formal analysis of composition independent of subject',
        'The material and technical construction of the object',
        'The deeper cultural symptoms a work unconsciously expresses',
      ],
      0,
      'That last one is iconology, his third and most ambitious level - and the one most often collapsed into the second.',
    ],
  ],
  phd: [
    [
      'What is the "period eye" as Baxandall uses it?',
      [
        'The culturally trained visual skills a contemporary viewer brought to a picture',
        'The characteristic viewpoint from which a fresco was designed to be seen',
        'The optical distortions introduced by early lens-based drawing aids',
        'The conventional palette available to painters in a given decade',
      ],
      0,
      'Gauging barrels and dancing taught quattrocento viewers to read volume and proportion - a social history of skills rather than of taste.',
    ],
    [
      'Riegl\'s concept of "Kunstwollen" was intended to do what?',
      [
        'Explain stylistic change as directed artistic will rather than as decline or technical failure',
        'Establish an objective measure of aesthetic quality across periods',
        'Reduce stylistic development to changes in available materials, pigments and supports above all else',
        'Attribute stylistic change to the psychology of individual masters',
      ],
      0,
      'It rehabilitated late Roman art, which the Renaissance model could only read as degeneration.',
    ],
    [
      'What does "disegno" mean in sixteenth-century Italian art theory?',
      [
        'Design as intellectual conception, taken as the foundation of all the arts',
        'The technique of drawing in silverpoint on prepared paper',
        'The contract specifying a commission\'s subject and materials',
        'The proportional grid used to transfer a design to a wall',
      ],
      0,
      'Vasari\'s central term, and the basis of the Florentine claim that painting is a liberal rather than a mechanical art.',
    ],
    [
      'What is the technical significance of the "camera obscura" debate around Vermeer?',
      [
        'Whether optical aids explain specific pictorial effects',
        'Whether Vermeer used a lens to grind his own pigments',
        'Whether the paintings were made as projections onto glass',
        'Whether his palette can only be explained by optical mixing',
      ],
      0,
      'Steadman and Wheelock argue the case; the interesting question is not whether he could have but what the "circles of confusion" actually prove.',
    ],
    [
      'Byzantine iconoclasm\'s theological argument turned on what?',
      [
        'Whether an image of Christ necessarily divides or confuses his two natures',
        'Whether images could be venerated in private but not in public worship',
        'Whether the second commandment applied to Christians at all',
        'Whether only relics, not images, could mediate divine presence',
      ],
      0,
      'John of Damascus and later Nikephoros answer via the Incarnation - the depictability of Christ follows from his having been seen.',
    ],
    [
      'What does "site-specificity" mean in post-1960s sculpture, and where does the argument bite?',
      [
        'A work constituted by its location',
        'A work designed to be reproducible in any location without alteration',
        'A work made from materials found at the site of installation',
        'A work whose meaning depends on the identity of its commissioner',
      ],
      0,
      'Serra lost, and the removal is now the standard case for what the claim entails legally as well as critically.',
    ],
    [
      'What is the "Nazarene" movement, and why does it matter for nineteenth-century painting?',
      [
        'A German brotherhood reviving pre-Raphaelite Italian models',
        'A French academic school defending Davidian neoclassicism',
        'An Italian revival of Byzantine icon painting techniques',
        'A Russian group of realist painters seceding from the Academy',
      ],
      0,
      'The Lukasbund in Rome, from 1809. The Peredvizhniki are the Russian secession, and a separate story.',
    ],
    [
      'Alois Riegl and Wölfflin differ chiefly in what respect?',
      [
        'Riegl explains style by directed will, Wolfflin by recurring formal polarities',
        'Riegl studies painting only, Wolfflin architecture only',
        'Riegl is concerned with patronage, Wolfflin with technique',
        'Riegl relies on connoisseurship, Wolfflin on documentary evidence',
      ],
      0,
      'Wolfflin\'s five paired categories - linear versus painterly and so on - are a descriptive apparatus, not a causal account, which is the criticism made of them.',
    ],
    [
      'What did the Munich "Degenerate Art" exhibition of 1937 do?',
      [
        'Displayed confiscated modernist works with hostile captions to discredit them publicly',
        'Presented state-approved art as a model for German painters',
        'Sold confiscated works abroad to raise foreign currency for the state rearmament programme',
        'Documented works destroyed in the preceding purge of museums',
      ],
      0,
      'It drew far larger crowds than the approved exhibition staged alongside it, which is the detail the organisers did not publicise.',
    ],
    [
      'The "Vitruvian" tradition in Renaissance architecture rested on what problem?',
      [
        'Vitruvius\'s text survived without its illustrations',
        'Vitruvius was known only in Arabic translation until the sixteenth century',
        'The text was attributed to Vitruvius but written centuries later',
        'Vitruvius described Greek rather than Roman practice throughout',
      ],
      0,
      'Which is why Alberti, Serlio and Palladio all differ on the orders while all claiming the same authority.',
    ],
  ],
  professor: [
    [
      'What is Wölfflin\'s methodological limitation as identified by later critics?',
      [
        'His formal polarities describe change without accounting for why it occurs',
        'His categories apply only to Italian art of two centuries',
        'He relied on reproductions rather than direct observation',
        'He rejected attribution questions as irrelevant to style',
      ],
      0,
      'Comparison without causation. It is exactly the gap the social history of art moved into.',
    ],
    [
      'What does Krauss\'s "sculpture in the expanded field" propose?',
      [
        'A structural map of postmodern practice in which sculpture is defined by what it is not',
        'That sculpture must be understood through the phenomenology of the viewer\'s body',
        'That minimalism exhausted the possibilities of the freestanding object',
        'That land art constitutes a distinct medium with its own history',
      ],
      0,
      '1979, and the Klein-group diagram is the part everyone remembers. Its formalism is also what its critics attack.',
    ],
    [
      'The "Warburg Institute" method is distinguished by what?',
      [
        'Tracing the migration of visual formulae and their emotional charge across periods and media',
        'The systematic photographic documentation of a national artistic patrimony',
        'The reconstruction of lost works from documentary evidence',
        'The attribution of anonymous works through connoisseurship',
      ],
      0,
      'Warburg\'s library was arranged by "the law of the good neighbour" - the shelf order was itself the argument.',
    ],
    [
      'What is the significance of technical art history\'s use of macro-XRF scanning?',
      [
        'Element-mapped images reveal underpaintings and pigment distribution non-destructively across a whole canvas',
        'It dates a painting absolutely from the decay of pigment isotopes',
        'It identifies the workshop of origin from the canvas weave alone',
        'It measures craquelure patterns to detect later restoration, distinguishing original paint from subsequent campaigns',
      ],
      0,
      'Weave matching is a separate and genuine technique. XRF is what turned "there is something underneath" into a legible image.',
    ],
    [
      'What does "the optical unconscious" name in Krauss\'s account?',
      [
        'A repressed, corporeal current in modernism against the disembodied opticality Greenberg privileged',
        'The involuntary afterimages exploited by op art, and the perceptual instability they produce in the viewer',
        'The unconscious influence of photography on painting composition',
        'The perceptual mechanisms studied by Gestalt psychology',
      ],
      0,
      'It is an argument with Greenberg\'s modernism as much as a history of it, which is a fair description of most of her work.',
    ],
    [
      'The debate over the Getty kouros concerns what?',
      [
        'Whether stylistic anomaly or scientific surface analysis should settle an authenticity question',
        'Whether Greek marble sculpture was originally painted, and how surviving pigment traces should be read',
        'Whether the figure was a grave marker or a votive dedication',
        'Whether the type derives from Egyptian or Near Eastern models',
      ],
      0,
      'Dolomite weathering can be faked and connoisseurs disagreed. The museum eventually relabelled it "about 530 BC or modern forgery", which is an unusually honest catalogue entry.',
    ],
    [
      'What is "the mobility of the picture plane" in accounts of Islamic ornament?',
      [
        'Pattern refuses a single fixed viewpoint',
        'Ornament was designed to be viewed from a moving procession',
        'Tiles were rearranged periodically to change a room\'s appearance',
        'Geometric pattern encodes an intentionally ambiguous perspective',
      ],
      0,
      'Grabar\'s and later Necipoglu\'s work on this is what displaced the older reading of ornament as horror vacui.',
    ],
    [
      'What did the Bauhaus preliminary course (Vorkurs) under Itten aim to do?',
      [
        'Strip students of received conventions before any craft training',
        'Teach draughtsmanship to a common standard before workshop allocation',
        'Provide theoretical instruction in colour science and perspective',
        'Test students\' aptitude for particular industrial trades',
      ],
      0,
      'Moholy-Nagy and Albers reworked it after Itten left. Its descendants are in every foundation year still running.',
    ],
    [
      'The "New Art History" of the 1970s and 80s is characterised by what?',
      [
        'Importing Marxist, feminist and psychoanalytic method against connoisseurship and formal analysis',
        'A return to archival documentation of patronage and workshop practice',
        'The application of statistical methods to attribution problems',
        'The rejection of periodisation as an organising principle, in favour of purely thematic groupings',
      ],
      0,
      'Nochlin\'s "Why Have There Been No Great Women Artists?" is the piece that shows what the shift actually bought.',
    ],
    [
      'What problem does the concept of "the original" pose for architectural conservation?',
      [
        'A building\'s significant state may be any of several',
        'Original materials cannot be dated with sufficient precision',
        'Original drawings rarely survive for pre-modern buildings',
        'Original structural performance cannot be verified without testing to failure',
      ],
      0,
      'Ruskin against Viollet-le-Duc, still unresolved, and the reason the Venice Charter reads as a set of compromises.',
    ],
  ],
};

export const MORE_MUSIC: CategoryContent = {
  msc: [
    [
      'What is a Neapolitan chord\'s usual function?',
      [
        'A predominant chord, typically approaching the dominant',
        'A tonic substitute at the start of a phrase',
        'A dominant substitute resolving directly to the tonic',
        'A passing chord between two inversions of the tonic',
      ],
      0,
      'Built on the flattened supertonic and usually in first inversion, which puts the fourth degree in the bass and makes the approach smooth.',
    ],
    [
      'In a fugue, what is the answer?',
      [
        'The subject restated in another voice at the fifth or fourth',
        'The countersubject introduced against the original entry',
        'The subject inverted and stated in the bass',
        'The closing statement of the subject in the tonic',
      ],
      0,
      'Tonal versus real answers - whether intervals are adjusted to stay in key - is the first thing a counterpoint exercise tests.',
    ],
    [
      'The Baroque doctrine of affections held what?',
      [
        'Specific musical figures reliably arouse specific emotional states in the listener',
        'Music should imitate the natural sounds of the world it depicts',
        'The composer\'s own feeling is the proper subject of the work',
        'Emotional expression should be subordinated to formal proportion',
      ],
      0,
      'A rhetorical rather than a Romantic conception of expression: the composer deploys affects, not confessions.',
    ],
    [
      'What is a passing note?',
      [
        'A non-chord tone filling the interval between two harmony notes stepwise',
        'A chord tone sustained while the harmony changes around it',
        'A dissonance approached by leap and resolved by step',
        'An ornamental note a semitone above the following harmony note',
      ],
      0,
      'The sustained one is a suspension; the semitone above is an appoggiatura in most usages.',
    ],
    [
      'Which composer wrote the "Fantasia on a Theme by Thomas Tallis"?',
      [
        'Ralph Vaughan Williams',
        'Gustav Holst, in the same decade',
        'Edward Elgar, in his Introduction and Allegro',
        'Frederick Delius, in his Brigg Fair variations',
      ],
      0,
      '1910. The double string orchestra and the modal harmony are both direct arguments about English musical ancestry.',
    ],
    [
      'What does "through-composed" mean of a song?',
      [
        'New music continues throughout',
        'The accompaniment is continuous with no rests',
        'The text is set syllabically without melisma',
        'The song modulates without returning to the tonic',
      ],
      0,
      'Schubert\'s Erlkonig against a strophic setting is the standard comparison for what the choice buys.',
    ],
    [
      'The mbira dzavadzimu is associated with which musical tradition?',
      [
        'Shona ceremonial music of Zimbabwe',
        'Yoruba drumming ensembles of Nigeria',
        'Mande griot repertoire of the western Sahel',
        'Ethiopian liturgical chant of the Orthodox church',
      ],
      0,
      'Interlocking cyclic patterns with a spirit-possession ritual function, and a strong influence on later minimalist thinking about cycles.',
    ],
    [
      'What is a tone cluster?',
      [
        'A chord of adjacent scale or chromatic tones sounded simultaneously',
        'A rapid arpeggiation across the full range of the instrument',
        'A chord built entirely from stacked perfect fourths',
        'A sustained group of harmonics produced by light finger pressure',
      ],
      0,
      'Cowell theorised them and Ives used them; the forearm on the keyboard is the practical instruction.',
    ],
    [
      'In Indian classical music, what is a tala?',
      [
        'The metrical cycle of a fixed number of beats within which a performance unfolds',
        'The melodic framework of permitted pitches and characteristic phrases',
        'The improvised unmetered introduction preceding the composition',
        'The drone providing the tonal centre throughout',
      ],
      0,
      'The melodic framework is the raga, the introduction the alap, the drone the tanpura - four things routinely conflated.',
    ],
    [
      'What does an appoggiatura do?',
      [
        'Sounds a dissonance on the beat, resolving by step to a chord tone',
        'Anticipates the following harmony before the beat',
        'Fills a third with a stepwise passing note',
        'Sustains a chord tone across a change of harmony',
      ],
      0,
      'On the beat and approached by leap is the usual definition, though eighteenth-century usage is looser than modern textbooks suggest.',
    ],
  ],
  phd: [
    [
      'What does the term "Klangfarbenmelodie" denote?',
      [
        'A succession in which timbre carries the structural role melody normally would',
        'The doubling of a melodic line across several instruments for colour',
        'A melody derived from the overtone series of a fundamental',
        'The orchestration of a keyboard work for full ensemble',
      ],
      0,
      'Schoenberg\'s term in the 1911 Harmonielehre; the third of the Five Orchestral Pieces is the demonstration.',
    ],
    [
      'What is isorhythm in fourteenth and fifteenth-century motets?',
      [
        'The repetition of a rhythmic pattern in the tenor across successive statements of the melody',
        'The alignment of all voices in identical rhythm throughout',
        'The use of a single mensuration across every section, without any change of proportion between them',
        'The augmentation of the tenor by a fixed proportion at each entry',
      ],
      0,
      'Talea and color, and their non-coincidence is what generates the large-scale structure of a Machaut or Du Fay motet.',
    ],
    [
      'The "querelle des Bouffons" concerned what?',
      [
        'The relative merits of French and Italian operatic style in mid-eighteenth-century Paris',
        'The propriety of castrati in sacred music, and whether the practice could be defended theologically',
        'The primacy of words or music in setting dramatic text',
        'The admission of instrumental music to the Academie',
      ],
      0,
      'Rousseau took the Italian side against Rameau. The words-versus-music quarrel is the earlier prima la musica dispute.',
    ],
    [
      'What does "just intonation" sacrifice relative to equal temperament?',
      [
        'The ability to modulate freely, since intervals are pure only in limited key relationships',
        'The purity of the perfect fifth in every key, which becomes audibly narrow in remote tonalities',
        'The possibility of writing consonant thirds at all',
        'A consistent octave, which becomes slightly variable',
      ],
      0,
      'Pure intervals in one key means wolf intervals elsewhere. Equal temperament makes everything slightly wrong so that everything is equally usable.',
    ],
    [
      'What is a "Landini cadence"?',
      [
        'A cadence in which the upper voice descends to the sixth degree before rising to the octave',
        'A cadence in which the bass falls a fifth to the tonic',
        'A plagal cadence used to close a sacred work, with the bass falling from subdominant to tonic',
        'A cadence approached through a chain of parallel sixths',
      ],
      0,
      'The under-third cadence, ubiquitous in the Italian trecento and named after Landini rather than invented by him.',
    ],
    [
      'What does "sonata principle" in Rosen\'s sense claim?',
      [
        'Material presented outside the tonic must be resolved by later restatement within it',
        'A sonata must contain exactly two contrasting subjects',
        'The development must not introduce new thematic material',
        'The recapitulation must restate the exposition without alteration',
      ],
      0,
      'It reframes sonata form as a tonal argument rather than a template, which is why it survives so many exceptions to the template.',
    ],
    [
      'The gamelan tunings slendro and pelog differ how?',
      [
        'Slendro divides the octave into five roughly equal steps; pelog into seven unequal ones',
        'Slendro is a tuning and pelog a rhythmic mode',
        'Slendro is used for court music and pelog only for shadow theatre',
        'Slendro is fixed by standard and pelog varies by ensemble',
      ],
      0,
      'In practice every ensemble is tuned to itself, so absolute pitch varies while the interval structure is what identifies the system.',
    ],
    [
      'What is "musique concrète" and who named it?',
      [
        'Composition from recorded sound as material, named by Pierre Schaeffer',
        'Composition from electronically generated tones, named by Herbert Eimert',
        'Composition determined by chance procedures, named by John Cage',
        'Composition for tape and live performer, named by Karlheinz Stockhausen',
      ],
      0,
      'Paris versus Cologne: concrete sound against synthesised tone, and the two studios spent a decade disagreeing about it.',
    ],
    [
      'What does a figured-bass "6/4" over a bass note usually indicate in eighteenth-century practice?',
      [
        'A second-inversion chord, most often a cadential or passing formation rather than an independent harmony',
        'A first-inversion chord with an added sixth above the bass, functioning as a subdominant substitute at the cadence',
        'A suspension over the dominant resolving to 5/3',
        'A chord of the added sixth on the subdominant',
      ],
      0,
      'The cadential 6/4 is a dominant with displaced upper voices, which is why Schenkerian analysis refuses to call it a tonic at all.',
    ],
    [
      'What is the significance of the Notre Dame school for notation?',
      [
        'It developed rhythmic modes and the notation capable of specifying them',
        'It introduced the five-line staff in its modern form',
        'It first notated music in mensural rather than neumatic form',
        'It established movable-type printing of polyphony',
      ],
      0,
      'Leonin and Perotin. Once duration is specifiable, independent rhythmic voices become possible, and everything downstream follows.',
    ],
  ],
  professor: [
    [
      'What is the central claim of Schenker\'s "Der freie Satz"?',
      [
        'Tonal works elaborate a single fundamental structure through hierarchical levels of prolongation',
        'Harmonic progression is governed by the acoustic properties of the overtone series',
        'Form arises from thematic development independent of tonal structure',
        'Counterpoint and harmony are separate and irreducible systems',
      ],
      0,
      'Reductive analysis with a strong normative streak - and the polemics about which repertoire it can be applied to have never stopped.',
    ],
    [
      'What does "spectralism" take as its compositional premise?',
      [
        'The internal acoustic structure of sound itself as the material of harmony and form',
        'The mathematical permutation of pitch-class sets',
        'The stochastic distribution of events over time',
        'The use of microtonal scales derived from non-Western tunings',
      ],
      0,
      'Grisey and Murail, at IRCAM. Analysis of a spectrum becomes the chord, so timbre and harmony stop being separable categories.',
    ],
    [
      'The "New Musicology" of the late 1980s argued what?',
      [
        'That music is not autonomous',
        'That music theory should adopt the methods of cognitive science',
        'That the canon should be replaced by popular repertoire',
        'That performance practice should determine analytical categories',
      ],
      0,
      'McClary\'s Feminine Endings is the flashpoint. The autonomy claim is what was actually at stake, more than any specific reading.',
    ],
    [
      'What does Lewin\'s transformational theory shift the analytical question to?',
      [
        'From what objects a passage contains to what operations move between them',
        'From pitch to rhythm as the primary parameter of structure',
        'From the score to the recorded performance as the analytical object',
        'From the individual work to the corpus as the unit of analysis',
      ],
      0,
      'Generalized Musical Intervals and Transformations, 1987. The gesture, not the inventory - and neo-Riemannian analysis is what it grew into.',
    ],
    [
      'What is the "problem of the fourth" in medieval and Renaissance theory?',
      [
        'The perfect fourth is consonant by ratio yet was treated as requiring resolution in counterpoint',
        'The fourth cannot be tuned justly in any temperament then in use, unlike the fifth or the octave above it',
        'The fourth was absent from the modal system before the fifteenth century',
        'The fourth was forbidden in parallel motion but required at cadences',
      ],
      0,
      'A simple ratio that behaves like a dissonance. It is the standard case for the gap between acoustic theory and compositional practice.',
    ],
    [
      'What does Adorno mean by the "culture industry" as applied to music?',
      [
        'Standardised production presented as individual choice, training listeners in passive recognition',
        'The concentration of publishing and recording in a few firms',
        'The subordination of composition to state cultural policy, as under the socialist realist doctrine',
        'The replacement of live performance by recorded reproduction',
      ],
      0,
      'The pseudo-individuation argument is the sharp part, and also the most contested - it grants listeners very little.',
    ],
    [
      'The "Early Music" movement\'s claim to historically informed performance is criticised how?',
      [
        'Authenticity is unrecoverable and the resulting aesthetic is itself a twentieth-century taste',
        'Period instruments cannot be reconstructed with sufficient accuracy',
        'Historical treatises contradict each other on every substantive point',
        'Modern audiences cannot hear historical tuning as consonant',
      ],
      0,
      'Taruskin\'s argument: the movement produced a modernist sound - lean, fast, unsentimental - and called it history.',
    ],
    [
      'What does "metric dissonance" describe in Krebs\'s analytical framework?',
      [
        'Conflict between simultaneous or successive metrical layers within a passage',
        'Rhythmic patterns that cannot be notated in any single time signature',
        'A tempo relation that cannot be expressed as a simple ratio',
        'Accents that fall consistently against the notated barline',
      ],
      0,
      'Grouping and displacement dissonance are the two types, and Schumann is the repertoire it was built to explain.',
    ],
    [
      'What is the analytical difficulty with "post-tonal" prolongation?',
      [
        'Without a consonance-dissonance hierarchy there is no principled basis for saying which events are structural',
        'Set-class analysis cannot handle passages longer than a few bars',
        'Atonal works rarely repeat material closely enough to compare, so motivic identity cannot be established at all',
        'Serial procedures determine every event, leaving nothing to analyse',
      ],
      0,
      'Straus\'s "problem of prolongation in post-tonal music" states the conditions clearly, and mostly concludes they are not met.',
    ],
    [
      'What does the study of "sound studies" add to musicology?',
      [
        'It treats listening itself as historically constructed, extending analysis to noise, silence and reproduction',
        'It measures the acoustic properties of historical performance spaces',
        'It applies psychoacoustic experiment to questions of consonance, grounding aesthetic categories in measurable perception',
        'It documents endangered musical traditions through field recording',
      ],
      0,
      'Sterne\'s work on the history of sound reproduction is the reference point - the ear as an artefact rather than a constant.',
    ],
  ],
};

export const MORE_SCREEN: CategoryContent = {
  msc: [
    [
      'What is a match on action?',
      [
        'A cut that continues a movement begun in the previous shot',
        'A cut between two shots with matching graphic composition',
        'A cut from a character\'s look to what they see',
        'A cut that repeats the same action from a second angle',
      ],
      0,
      'Cutting from a look to its object is the eyeline match. Continuity editing runs on both, plus keeping to one side of the axis.',
    ],
    [
      'The French New Wave critics wrote principally for which journal?',
      [
        'Cahiers du Cinema',
        'Positif, its long-standing rival',
        'Sight and Sound, published in London',
        'Revue du Cinema, its pre-war predecessor',
      ],
      0,
      'Bazin co-founded it in 1951; Truffaut, Godard, Rohmer and Chabrol all wrote there before directing.',
    ],
    [
      'What does "mise-en-scene" cover?',
      [
        'Everything arranged before the camera: setting, lighting, costume, blocking',
        'The arrangement of shots in the edit, and the rhythm that arrangement establishes',
        'The relationship between image and soundtrack',
        'The movement of the camera within a shot',
      ],
      0,
      'Whether camera movement belongs inside it is a genuine terminological disagreement in the literature.',
    ],
    [
      'German Expressionist cinema is characterised by what?',
      [
        'Distorted sets and stylised lighting externalising psychological states',
        'Location shooting with non-professional actors',
        'Rapid montage constructed on dialectical principles',
        'Long takes with deep focus and minimal camera movement',
      ],
      0,
      'Caligari and Nosferatu. The other three describe neorealism, Soviet montage and Bazinian realism respectively.',
    ],
    [
      'What is a "MacGuffin"?',
      [
        'A plot device that motivates the characters while its own nature is immaterial',
        'A character introduced solely to be killed in the first act',
        'A visual motif repeated to signal a coming revelation',
        'An ending that reframes everything preceding it',
      ],
      0,
      'Hitchcock\'s term, and his own examples were deliberately interchangeable - which is the definition doing its work.',
    ],
    [
      'The Hays Code governed what?',
      [
        'The moral content of American studio films from the 1930s to the 1960s',
        'The trade practices of distributors in relation to exhibitors',
        'The employment of foreign nationals in Hollywood productions',
        'The technical standards for sound-on-film reproduction',
      ],
      0,
      'Self-regulation to avoid statutory censorship. Its collapse in the 1960s produced the ratings system that replaced it.',
    ],
    [
      'What does the term "showrunner" designate in television?',
      [
        'The lead writer-producer with creative authority over a series',
        'The executive at the network responsible for commissioning',
        'The head of production logistics for a shooting schedule',
        'The director assigned to establish a series\' visual style',
      ],
      0,
      'Writer-led authority is the structural difference between television and feature film production, and it is why the auteur debate runs differently there.',
    ],
    [
      'Italian neorealism is associated with which conditions of production?',
      [
        'Location shooting, non-professional actors and post-war material scarcity',
        'Studio construction of stylised environments',
        'State-funded production under a centralised film institute',
        'Multi-camera shooting for rapid turnaround',
      ],
      0,
      'The scarcity was real but the aesthetic was a choice: Cinecitta was standing and partly usable throughout.',
    ],
    [
      'What is "day for night"?',
      [
        'Shooting in daylight with filtration and underexposure to simulate night',
        'Shooting at dusk for the brief period of usable ambient light',
        'Shooting interiors during the day and exteriors at night',
        'Printing a daylight negative on high-contrast stock',
      ],
      0,
      'La nuit americaine in French, which is the Truffaut title and also a fair statement of where the technique was industrialised.',
    ],
    [
      'What does "the 30-degree rule" advise?',
      [
        'Change the camera angle by at least thirty degrees between successive shots of the same subject',
        'Keep the camera within thirty degrees of the eyeline axis',
        'Limit pans to thirty degrees per second to avoid strobing',
        'Place key lights at thirty degrees to the subject\'s face, measured from the lens axis at eye height',
      ],
      0,
      'Less than that reads as a jump cut - a mistake rather than a choice, unless it is deliberately the choice.',
    ],
  ],
  phd: [
    [
      'What does Metz\'s "grande syntagmatique" attempt?',
      [
        'A taxonomy of the ways shots can be ordered into narrative segments',
        'A grammar equating shots with words and sequences with sentences',
        'A theory of spectatorial identification derived from Lacan',
        'A classification of genres by their narrative economy',
      ],
      0,
      'Its usefulness is disputed and its ambition is the point: film semiotics trying to find units at the level above the shot.',
    ],
    [
      'What is "suture" in film theory?',
      [
        'The process by which shot-reverse-shot binds the spectator into the film\'s system of looks',
        'The seamless joining of two negatives in optical printing, so that the splice is invisible on screen',
        'The alignment of the soundtrack with the image at the mix',
        'The narrative resolution of an ambiguity left open earlier',
      ],
      0,
      'Oudart and Dayan out of Lacan. It is where 1970s theory locates ideology - in editing structure rather than in content.',
    ],
    [
      'Mulvey\'s "Visual Pleasure and Narrative Cinema" argues what?',
      [
        'Classical cinema structures looking as active and male',
        'Female spectators cannot identify with classical narrative at all',
        'Scopophilia is a universal rather than historically specific structure',
        'Cinema\'s pleasures are inseparable from its commercial form',
      ],
      0,
      '1975. Mulvey herself revisited the account of the female spectator, which the essay\'s reception often forgets.',
    ],
    [
      'What is "haptic visuality" as Marks uses it?',
      [
        'An image that invites touch-like, close, textural perception rather than optical mastery',
        'The use of tactile props to produce an embodied response',
        'The projection of film onto irregular surfaces, so the image takes on the texture of its support',
        'The correspondence of camera movement to bodily movement',
      ],
      0,
      'Developed for intercultural and diasporic cinema, where memory is often carried by texture rather than by narrative.',
    ],
    [
      'What did the Frankfurt School\'s Kracauer contribute in "Theory of Film"?',
      [
        'A realist argument that cinema\'s vocation is the redemption of physical reality',
        'A critique of cinema as the exemplary product of the culture industry',
        'An account of the cinematic apparatus as an ideological machine',
        'A phenomenology of the spectator\'s embodied perception',
      ],
      0,
      'It is closer to Bazin than to Adorno, which surprises people who file him by institution rather than by argument.',
    ],
    [
      'What is "the apparatus" in Baudry\'s account?',
      [
        'The whole projection situation',
        'The camera and lens as instruments imposing Renaissance perspective',
        'The studio system\'s division of labour',
        'The distribution network determining what can be seen',
      ],
      0,
      'The Platonic cave analogy is doing the heavy lifting, and the essay is as much psychoanalysis of the cinema-goer as film theory.',
    ],
    [
      'What does "cinema of attractions" describe?',
      [
        'Early cinema\'s address to the spectator through display and spectacle rather than narrative absorption',
        'The use of stars as the principal draw for audiences',
        'The fairground and vaudeville venues in which films were first shown',
        'Trick films made possible by stop-substitution, and the fairground exhibition circuits that carried them',
      ],
      0,
      'Gunning\'s term. Its power is that it makes early cinema something other than incompetent narrative.',
    ],
    [
      'What is the "180-degree system" argued to accomplish ideologically?',
      [
        'It naturalises a coherent, masterable space, hiding the constructedness of the scene',
        'It restricts the number of camera positions available and so cheapens production',
        'It privileges dialogue over action in scene construction',
        'It requires continuity of lighting between takes',
      ],
      0,
      'The 1970s critique treats classical style as an argument about knowledge, not merely a set of craft conventions.',
    ],
    [
      'What does Bordwell mean by "historical poetics" of cinema?',
      [
        'Studying how films are constructed and how those norms change, in preference to interpretive theory',
        'Reconstructing the historical reception of films from press archives',
        'Applying literary poetics to screenplay structure',
        'Dating films by their technical characteristics, from stock, perforation and sound-format evidence',
      ],
      0,
      'It is explicitly a rebuke to Grand Theory - problems and norms rather than doctrine, and its own critics call it formalism with a clean conscience.',
    ],
    [
      'What is the significance of the "Nouvelle Vague" politique des auteurs?',
      [
        'It asserted the director as the author of a film',
        'It demanded state subsidy for first-time directors',
        'It rejected literary adaptation in favour of original screenplays',
        'It insisted that criticism precede any practical film-making',
      ],
      0,
      'Sarris carried it into English as "auteur theory", and the mistranslation of a polemical position into a theory is itself part of the history.',
    ],
  ],
  professor: [
    [
      'What does Deleuze\'s distinction between the movement-image and the time-image turn on?',
      [
        'Whether time is presented indirectly through action or directly, freed from sensory-motor continuity',
        'Whether a film uses continuity editing or montage',
        'Whether narration is subjective or objective',
        'Whether the camera is mobile or static within the shot, and whether that movement is motivated by the action',
      ],
      0,
      'The break is located in post-war cinema, with neorealism as the hinge - a philosophical periodisation, not a stylistic one.',
    ],
    [
      'What is the "dispositif" turn in recent screen studies concerned with?',
      [
        'The arrangement of screens, spaces and habits through which moving images are encountered',
        'The equipment used on set and its effect on style, from lens choice through to camera support',
        'The legal frameworks governing distribution rights',
        'The disposition of shots within a single sequence',
      ],
      0,
      'It is how the field absorbed phones, galleries and streaming without pretending they are just more cinema.',
    ],
    [
      'The "post-cinematic" argument claims what?',
      [
        'Digital production and distribution have altered the medium\'s ontology',
        'Cinema-going has declined to the point of cultural irrelevance',
        'Digital images are indistinguishable from photographic ones in principle',
        'Film theory should be replaced by media archaeology',
      ],
      0,
      'The indexical link to a profilmic event is what is claimed to be lost - Rodowick and Shaviro press the case, and Bazinians are unimpressed.',
    ],
    [
      'What did the Barthes essay "The Third Meaning" propose about the film still?',
      [
        'A level of signification beyond the informational and the symbolic, resistant to paraphrase',
        'That stills falsify films by arresting their movement, so no still can carry any filmic meaning',
        'That publicity images determine how a film is received',
        'That montage produces meaning absent from any individual frame',
      ],
      0,
      'Written on Eisenstein stills. The obtuse meaning is the ancestor of what he later called the punctum in photography.',
    ],
    [
      'What is the methodological problem with "screen memory" in trauma-and-cinema scholarship?',
      [
        'It borrows a clinical concept for cultural analysis without the evidentiary conditions the clinical use assumes',
        'Trauma cannot be represented in any medium, making the object empty',
        'Testimony and fiction cannot be distinguished in archival footage, so provenance work is a precondition of any reading',
        'The concept applies only to individual and never to collective memory',
      ],
      0,
      'Not an argument against the work, but the reason the good work in this area is so careful about what it claims to have shown.',
    ],
    [
      'What does research on "attention economies" contribute to television studies?',
      [
        'It reframes scheduling, seriality and platform recommendation as competing for a scarce resource',
        'It measures audience size more accurately than ratings panels',
        'It explains genre cycles by production cost alone, treating audience preference as effectively constant throughout',
        'It treats viewers as rational consumers of narrative goods',
      ],
      0,
      'It is why binge release and episodic scheduling are legible as different bets on the same scarcity rather than as simple technical successors.',
    ],
    [
      'What is at stake in the debate over "quality television" as a category?',
      [
        'Whether the label describes formal properties or simply legitimises particular tastes and demographics',
        'Whether television can sustain authorship in the cinematic sense',
        'Whether serial narrative is inherently superior to episodic, and whether the distinction tracks anything formal',
        'Whether subscription funding produces better writing than advertising',
      ],
      0,
      'Newman and Levine\'s "Legitimating Television" is the sceptical case, and it is largely about class and gender rather than about craft.',
    ],
    [
      'What does the concept of "media convergence" as Jenkins uses it emphasise?',
      [
        'Cultural and participatory shifts in how audiences circulate content, not merely technological merging of devices',
        'The consolidation of media ownership into fewer conglomerates',
        'The unification of transmission standards across platforms',
        'The replacement of broadcast by on-demand distribution, and the scheduling practices that followed from that shift',
      ],
      0,
      'The distinction from the ownership story matters: Jenkins is describing audience labour, and the political economy critique is that this understates who captures its value.',
    ],
    [
      'The archival turn in film history changed the field how?',
      [
        'Access to non-canonical prints and paper records displaced accounts built from the surviving canon',
        'It established digital restoration as the standard scholarly edition',
        'It shifted attention from production to reception exclusively',
        'It proved that most silent films survive in some form, contrary to received estimates of the scale of loss',
      ],
      0,
      'The Brighton 1978 FIAF congress is the usual marker. Roughly three-quarters of silent-era production is lost, which is exactly the point about canon formation.',
    ],
    [
      'What problem does "world cinema" as a category share with "world literature"?',
      [
        'It risks defining everything by its distance from a metropolitan centre it leaves unmarked',
        'It cannot accommodate co-productions with multiple national origins',
        'It depends on festival programming, which is not scholarly evidence',
        'It excludes documentary and experimental practice by construction',
      ],
      0,
      'The residual-category problem: "world" ends up meaning "not Hollywood", which tells you about the classifier rather than the films.',
    ],
  ],
};
